/**
 * PropertyDataReview Helper Components
 *
 * Extracted helper components and utility functions for PropertyDataReview:
 * - DataField - Displays a labeled data field with optional icon
 * - formatPrice - Formats price with currency
 * - Section components - BasicInfoSection, SpecificationsSection, etc.
 */

import { Tag, Bed, Bath, Square, Calendar, MapPin, Building, Navigation } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ReviewImagesGallery } from './ReviewImagesGallery';
import type {
  IDataFieldProps,
  IBasicInfoSectionProps,
  ISpecificationsSectionProps,
  ILocationSectionProps,
  IFeaturesSectionProps,
  IDescriptionSectionProps,
  IImagesSectionProps,
} from '@/@types/property';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format price with currency
 * @param price - The price value (undefined if not available)
 * @param currency - The currency code (defaults to EUR)
 * @returns Formatted price string
 */
function formatPrice(price: number | undefined, currency: string | undefined): string {
  if (price === undefined) {
    return 'Not specified';
  }

  const currencyCode = currency || 'EUR';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

// ============================================================================
// Helper Components
// ============================================================================

/**
 * Helper component to display a data field
 */
function DataField({ label, value, icon }: IDataFieldProps) {
  const displayValue = value !== undefined ? String(value) : 'Not specified';

  return (
    <div className="flex items-start gap-2">
      {icon && (
        <span className="mt-0.5 text-muted-foreground" aria-hidden="true">
          {icon}
        </span>
      )}
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-sm font-medium">{displayValue}</span>
      </div>
    </div>
  );
}

// ============================================================================
// Section Components
// ============================================================================

/**
 * Basic info section content (title, property type, price)
 */
export function BasicInfoSection({ title, propertyType, price, currency }: IBasicInfoSectionProps) {
  return (
    <div className="space-y-4">
      <div>
        <span className="text-xs text-muted-foreground">Title</span>
        <p className="text-sm font-medium">{title}</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <DataField label="Property Type" value={propertyType} icon={<Tag className="h-4 w-4" />} />
        <DataField
          label="Price"
          value={formatPrice(price, currency)}
          icon={<span className="text-sm font-medium">{currency || 'EUR'}</span>}
        />
      </div>
    </div>
  );
}

/**
 * Specifications section content (beds, baths, sqm, year)
 */
export function SpecificationsSection({
  bedrooms,
  bathrooms,
  squareMeters,
  yearBuilt,
}: ISpecificationsSectionProps) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <DataField label="Bedrooms" value={bedrooms} icon={<Bed className="h-4 w-4" />} />
      <DataField label="Bathrooms" value={bathrooms} icon={<Bath className="h-4 w-4" />} />
      <DataField
        label="Square Meters"
        value={squareMeters ? `${squareMeters} m²` : undefined}
        icon={<Square className="h-4 w-4" />}
      />
      <DataField label="Year Built" value={yearBuilt} icon={<Calendar className="h-4 w-4" />} />
    </div>
  );
}

/**
 * Format coordinates for display
 */
function formatCoordinates(coordinates: { lat: number; lng: number } | undefined): string {
  if (!coordinates) {
    return 'Not specified';
  }
  return `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`;
}

/**
 * Location section content (address, city, coordinates)
 */
export function LocationSection({ address, city, coordinates }: ILocationSectionProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <DataField label="Address" value={address} icon={<MapPin className="h-4 w-4" />} />
      <DataField label="City" value={city} icon={<Building className="h-4 w-4" />} />
      <DataField
        label="Coordinates"
        value={formatCoordinates(coordinates)}
        icon={<Navigation className="h-4 w-4" />}
      />
    </div>
  );
}

/**
 * Features section content (list of amenities)
 */
export function FeaturesSection({ features }: IFeaturesSectionProps) {
  const hasFeatures = features && features.length > 0;

  if (!hasFeatures) {
    return (
      <p className="text-sm text-muted-foreground">No features were found for this property.</p>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {features?.map(feature => (
        <Badge key={feature} variant="outline">
          {feature}
        </Badge>
      ))}
    </div>
  );
}

/**
 * Description section content (AI-generated description)
 */
export function DescriptionSection({ description }: IDescriptionSectionProps) {
  const hasDescription = description && description.trim().length > 0;

  if (!hasDescription) {
    return <p className="text-sm text-muted-foreground">No description has been generated yet.</p>;
  }

  return (
    <Textarea
      value={description}
      readOnly
      className="min-h-32 resize-none bg-muted/50"
      aria-label="Property description"
    />
  );
}

/**
 * Images section content (gallery with lightbox, drag-drop, and delete)
 */
export function ImagesSection({ imageIds, propertyId }: IImagesSectionProps) {
  const hasImages = imageIds && imageIds.length > 0;

  if (!hasImages) {
    return <p className="text-sm text-muted-foreground">No images were found for this property.</p>;
  }

  return <ReviewImagesGallery imageIds={imageIds} propertyId={propertyId} />;
}
